import React from "react";
import { reportReactRenderError } from "../../observability/sentry";
import { getLocale, type MessageKey } from "../../i18n";
import { translate } from "../../i18n/translate";

type ErrorKind = "chunk" | "network" | "render";

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
  errorKind: ErrorKind;
}

function classifyError(error: Error): ErrorKind {
  const message = error.message.toLowerCase();
  const name = error.name.toLowerCase();

  if (
    name.includes("chunkloaderror") ||
    message.includes("chunk") ||
    message.includes("dynamically imported module")
  ) {
    return "chunk";
  }

  if (
    message.includes("network") ||
    message.includes("failed to fetch") ||
    message.includes("load failed")
  ) {
    return "network";
  }

  return "render";
}

function errorScreenCopy(kind: ErrorKind): {
  title: MessageKey;
  message: MessageKey;
} {
  switch (kind) {
    case "chunk":
      return {
        title: "errorBoundary.chunkTitle",
        message: "errorBoundary.chunkMessage",
      };
    case "network":
      return {
        title: "errorBoundary.networkTitle",
        message: "errorBoundary.networkMessage",
      };
    case "render":
      return {
        title: "errorBoundary.renderTitle",
        message: "errorBoundary.renderMessage",
      };
  }
}

export class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = {
    error: null,
    errorKind: "render",
  };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      error,
      errorKind: classifyError(error),
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error("React error boundary caught an error", error, errorInfo);
    reportReactRenderError(error, errorInfo);
  }

  render(): React.ReactNode {
    const { error, errorKind } = this.state;
    if (!error) return this.props.children;

    const copy = errorScreenCopy(errorKind);
    const locale = getLocale();

    return (
      <main
        role="alert"
        className="min-h-screen bg-slate-950 text-white flex items-center justify-center p-6"
      >
        <section className="max-w-lg w-full rounded-lg border border-white/15 bg-white/10 p-6 shadow-xl">
          <p className="text-xs uppercase tracking-wide text-cyan-200">
            {translate(locale, copy.title)}
          </p>
          <h1 className="mt-2 text-2xl font-semibold">
            {translate(locale, "errorBoundary.unableToLoad")}
          </h1>
          <p className="mt-3 text-sm text-slate-200">
            {translate(locale, copy.message)}
          </p>
          <p className="mt-4 rounded bg-black/30 p-3 font-mono text-xs text-slate-200 break-words">
            {error.message}
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-5 rounded bg-cyan-400 px-4 py-2 text-sm font-semibold text-slate-950 hover:bg-cyan-300"
          >
            {translate(locale, "errorBoundary.reload")}
          </button>
        </section>
      </main>
    );
  }
}
